import { Key } from 'react';
import {
  TableState as OriginalTableState,
  TableStateProps,
  useTableState as useOriginalTableState,
} from '@react-stately/table';
import { getChildNodes } from '@react-stately/collections';
import { SapphireTableCollection } from './SapphireTableCollection';
import patchSelectionManager from '../../utils/patchSelectionManager';
import { Selection } from '@react-types/shared/src/selection';
import { SelectionManager } from '@react-stately/selection';

export interface SapphireTableState<T>
  extends Omit<OriginalTableState<T>, 'collection'> {
  collection: SapphireTableCollection<T>;
}

export type SapphireTableStateProps<T extends object> = Omit<
  TableStateProps<T>,
  'collection' | 'onSelectionChange'
> & {
  collection: SapphireTableCollection<T>;
  /**
   * Disables selection and row action on the entire table
   * @default false
   */
  isDisabled?: boolean;
  onSelectionChange?: (selection: Selection) => void;
  resolveSelectAllToKeys?: boolean;
};

export function useTableState<T extends object>({
  isDisabled = false,
  resolveSelectAllToKeys = false,
  collection,
  onSelectionChange,
  ...props
}: SapphireTableStateProps<T>): SapphireTableState<T> {
  const state = useOriginalTableState({
    onSelectionChange,
    ...props,
    children: undefined,
    collection,
  });

  const selectionManager = state.selectionManager as SelectionManager & {
    [ORIGINAL_CAN_SELECT_ITEM]?: SelectionManager['canSelectItem'];
  };
  patchSelectionManager(state.selectionManager, {
    resolveSelectAllToKeys,
    isDisabled,
  });

  // patch canSelectItem to support nonSelectable prop and exclude footer rows from selection.
  selectionManager[ORIGINAL_CAN_SELECT_ITEM] =
    selectionManager[ORIGINAL_CAN_SELECT_ITEM] ??
    state.selectionManager.canSelectItem.bind(selectionManager);
  selectionManager.canSelectItem = function (key: Key): boolean {
    // TODO: add a test case to cover this
    const isFooterRow = [
      ...getChildNodes(
        (state.collection as SapphireTableCollection<unknown>).footer,
        state.collection
      ),
    ].find((row) => row.key === key);
    const item = state.collection.getItem(key);
    return (
      !isDisabled &&
      !item?.props.INTERNAL_nonSelectable &&
      !isFooterRow &&
      selectionManager[ORIGINAL_CAN_SELECT_ITEM]!(key)
    );
  };

  return { ...state, collection };
}

const ORIGINAL_CAN_SELECT_ITEM = Symbol('ORIGINAL_CAN_SELECT_ITEM');
